import TaskModalOpener from '@monorepo/old-web/js/components/task/TaskModalOpener';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { EditLogEntryModal } from '@monorepo/shared/componentsV2/logs/EditLogEntryModal';
import { LogEntriesUploadModal } from '@monorepo/shared/componentsV2/logs/LogEntriesUploadModal/LogEntriesUploadModal';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import { endOfDay, isPast, startOfDay } from 'date-fns';
import { DateLike, localEquivalentOfUTC } from 'mapistry-shared';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LogEventsModal } from './LogEventsModal';

export function ComplianceCalendarPageModals() {
  const { project } = useCurrentProject();

  const [urlQueryParams, setUrlQueryParams] = useSearchParams();
  const logId = urlQueryParams.get('logId');
  const dueDate = urlQueryParams.get('dueDate');
  const taskId = urlQueryParams.get('taskId');

  const [isLogEntryModalOpen, openLogEntryModal, closeLogEntryModal] =
    useOpenClose();
  const [isBulkUploadModalOpen, openBulkUploadModal, closeBulkUploadModal] =
    useOpenClose();

  /**
   * Save off the logId and due date for the upload and entry modals.
   * We need to close the LogEventsModal when we open the other modals
   * otherwise the LogEventsModal continuously steals the focus from
   * the newly opened modals. When we close the LogEventsModal we will
   * lose the logId and dueDate because it navigates to the base
   * compliance calendar page
   */
  const [logIdToAddEntriesTo, setLogIdToAddEntriesTo] = useState<
    string | undefined
  >();
  const [dueDateForLogEvent, setDueDateForLogEvent] = useState<
    DateLike | undefined
  >();

  const logDate = useMemo(() => {
    // If no information about calendar event, allow the form to set default log date itself
    if (!dueDateForLogEvent) {
      return undefined;
    }
    const dueDateInLocalTime = localEquivalentOfUTC(
      new Date(dueDateForLogEvent),
    );
    const isOverdue = isPast(endOfDay(dueDateInLocalTime));
    return isOverdue ? startOfDay(dueDateInLocalTime) : new Date();
  }, [dueDateForLogEvent]);

  useEffect(() => {
    if (logId) {
      setLogIdToAddEntriesTo(logId);
    }
  }, [logId]);

  useEffect(() => {
    if (dueDate) {
      setDueDateForLogEvent(dueDate);
    }
  }, [dueDate]);

  return (
    <>
      {taskId && project && (
        <TaskModalOpener
          taskId={taskId}
          projectId={project.id}
          dueDate={dueDate}
        />
      )}
      {logId && (
        <ErrorBoundary onError={() => setUrlQueryParams('')}>
          <LogEventsModal
            dueDate={dueDate}
            logId={logId}
            openLogEntryModal={openLogEntryModal}
            openUploadModal={openBulkUploadModal}
          />
        </ErrorBoundary>
      )}
      {isLogEntryModalOpen && logIdToAddEntriesTo && (
        <ErrorBoundary onError={closeLogEntryModal}>
          <EditLogEntryModal
            logDate={logDate}
            logId={logIdToAddEntriesTo}
            onClose={closeLogEntryModal}
            open={isLogEntryModalOpen}
            organizationId={project?.organization_id}
            projectId={project?.id}
          />
        </ErrorBoundary>
      )}
      {logIdToAddEntriesTo && project && (
        <ErrorBoundary onError={closeBulkUploadModal}>
          <LogEntriesUploadModal
            isOpen={isBulkUploadModalOpen}
            logId={logIdToAddEntriesTo}
            onClose={closeBulkUploadModal}
            organizationId={project.organization_id}
            projectId={project.id}
          />
        </ErrorBoundary>
      )}
    </>
  );
}
